import { ActionIcon, Flex } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconSend } from "@tabler/icons-react";
import { useCallback, useState } from "react";

import { useScrubbableAudio } from "../../../hooks/useScrubbableAudio";
import type { RecordingOutput } from "../../../hooks/useMicrophone";
import { apiService } from "../../../services/api.service";
import type { VoiceUrls } from "../../../types";
import type { StaticTextTranslator } from "../../../utils";

import { PlaybackControl } from "./PlaybackControl";
import { PlaybackControlVariant } from "./playback-control-variant";

import styles from "./Playback.module.css";

interface PlaybackProps {
  canSkip?: boolean;
  onSubmit: (audioKey: string) => void | Promise<void>;
  onUploadError: () => void;
  onUploadStart: () => void;
  onUploadSuccess: () => void;
  recording: RecordingOutput;
  reset: () => void;
  translate: StaticTextTranslator;
  voiceUrls: VoiceUrls;
}

export const Playback = ({
  canSkip,
  onSubmit,
  onUploadError,
  onUploadStart,
  onUploadSuccess,
  recording: { blob, duration, srcUrl },
  reset,
  translate,
  voiceUrls,
}: PlaybackProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    audioElement,
    audioRef,
    durationElement,
    playWithCatch,
    playing,
    scrubbingElement,
    setPlaybackTime,
  } = useScrubbableAudio({ disabled: isLoading, duration, srcUrl, translate });

  const uploadAndSend = useCallback(async () => {
    audioRef?.pause();
    setIsLoading(true);
    onUploadStart();

    const persistentFailureHint = canSkip
      ? translate(
          "Please try again, or use the Skip button if the problem persists.",
        )
      : translate("Please try again.");

    try {
      await apiService.uploadVoiceResponse(voiceUrls.uploadUrl, blob);
    } catch {
      onUploadError();
      notifications.show({
        message: `${translate("There was a problem uploading your response.")} ${persistentFailureHint}`,
        color: "red",
      });
      setIsLoading(false);
      return;
    }

    try {
      await onSubmit(voiceUrls.audioUrl);
      onUploadSuccess();
    } catch {
      onUploadError();
      notifications.show({
        message: `${translate("There was a problem sending your answer.")} ${persistentFailureHint}`,
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    audioRef,
    blob,
    canSkip,
    onSubmit,
    onUploadError,
    onUploadStart,
    onUploadSuccess,
    translate,
    voiceUrls.audioUrl,
    voiceUrls.uploadUrl,
  ]);

  return (
    <Flex className={styles.wrapper}>
      <Flex className={styles.controlsWrapper}>
        <PlaybackControl
          isLoading={isLoading}
          onClick={() => {
            if (playing) {
              audioRef?.pause();
              setPlaybackTime(0);
            } else {
              playWithCatch();
            }
          }}
          variant={
            playing ? PlaybackControlVariant.Stop : PlaybackControlVariant.Play
          }
          translate={translate}
        />
        <PlaybackControl
          isLoading={isLoading}
          onClick={reset}
          variant={PlaybackControlVariant.Delete}
          translate={translate}
        />

        {audioElement}
        {scrubbingElement}
        {durationElement}

        <ActionIcon
          aria-label={translate("Submit voice message")}
          className={styles.sendButton}
          loading={isLoading}
          onClick={uploadAndSend}
          variant="subtle"
        >
          <IconSend className={styles.sendIcon} size={20} />
        </ActionIcon>
      </Flex>
    </Flex>
  );
};
